'use client';

import { useEffect, useRef, useTransition } from 'react';
import type { SessionData } from '@/src/server/session/session.helper';
import type { TenantEntry } from '@/src/lib/tenant-switcher-storage';
import type { NavigationContext } from '@/src/lib/navigation';
import { ContextTabs } from './context-tabs';
import { LocaleSwitcher } from './locale-switcher';
import { PowerModeToggle } from './power-mode-toggle';
import { ThemeToggle } from './theme-toggle';
import { AppBreadcrumbs } from './breadcrumbs';
import { GlobalSearchBox } from './global-search-box';
import { MobileNav } from './mobile-nav';
import { NotificationBell } from './notification-bell';
import { EnhancedTenantSwitcher } from './enhanced-tenant-switcher';
import { UserMenu } from './user-menu';
import { OverflowMenu } from './overflow-menu';
import { CommandPaletteTrigger } from '@/src/components/command-palette/command-palette-trigger';
import { InlineHelp } from '@/src/components/ui-kontora/inline-help';
import type { ResolvedNavigationSection } from '@/src/lib/navigation';
import { switchTenantAction } from '@/src/server/actions/access-control.actions';
import { QuickActionsBar } from './quick-actions-bar';
import { observeStickyScrollPadding } from '@/src/lib/sticky-scroll-padding';

export interface TopbarProps {
  session: SessionData;
  tenants: TenantEntry[];
  sections: ResolvedNavigationSection[];
  unreadNotifications: number | null;
  roleLevel: 'superuser' | 'admin' | 'manager' | 'user' | 'readonly';
  activeContext: NavigationContext;
  availableContexts: readonly NavigationContext[];
  powerMode: boolean;
  canUsePowerMode: boolean;
  permissions?: readonly string[];
}

export function Topbar({
  session,
  tenants,
  sections,
  unreadNotifications,
  activeContext,
  availableContexts,
  powerMode,
  canUsePowerMode,
  permissions = [],
}: TopbarProps) {
  const [, startTenantSwitch] = useTransition();
  const headerRef = useRef<HTMLElement>(null);
  const activeTenantId = session.activeTenantPublicId ?? '';

  useEffect(() => {
    if (!headerRef.current) return;
    return observeStickyScrollPadding(
      headerRef.current,
      document.querySelector<HTMLElement>('[data-tour="mobile-bottom-nav"]'),
    );
  }, []);

  function handleTenantSwitch(tenantId: string) {
    if (tenantId === activeTenantId) return;
    startTenantSwitch(async () => {
      await switchTenantAction(tenantId);
    });
  }

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-30 border-b border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--k-color-surface)]/85"
    >
      <div className="px-4 py-3 xl:px-6">
        <div className="mb-2 hidden lg:block">
          <AppBreadcrumbs />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <MobileNav
            sections={sections}
            activeContext={activeContext}
            powerMode={powerMode}
            availableContexts={availableContexts}
          />
          <div className="min-w-0 flex-1 lg:hidden">
            <AppBreadcrumbs />
          </div>
          <div className="hidden lg:flex lg:items-center lg:gap-2">
            <EnhancedTenantSwitcher
              tenants={tenants}
              activeTenantId={activeTenantId}
              onSwitch={handleTenantSwitch}
            />
          </div>
          {canUsePowerMode ? (
            <span className="inline-flex items-center gap-1">
              <PowerModeToggle activeContext={activeContext} enabled={powerMode} />
              <InlineHelp
                term="Power-Mode"
                description="Sichtbarkeit aller Bereiche unabhängig von Rollen-Rechten. Ausschließlich für Sysadmins, jeder Toggle wird auditiert."
                learnMoreHref="/docs/rollen#power-mode"
              />
            </span>
          ) : null}
          <LocaleSwitcher />
          <ThemeToggle />
          <OverflowMenu
            activeContext={activeContext}
            availableContexts={availableContexts}
            canUsePowerMode={canUsePowerMode}
            userDisplayName={session.displayName ?? ''}
            userPermissions={permissions}
            currentUserId={session.userId}
          />
          <NotificationBell count={unreadNotifications} />
          <CommandPaletteTrigger />
          <UserMenu session={session} />
        </div>
        <div className="mt-3 flex min-w-0 flex-col gap-3">
          <ContextTabs activeContext={activeContext} availableContexts={availableContexts} />
        </div>
        <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <GlobalSearchBox />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <QuickActionsBar
              activeContext={activeContext}
              permissions={permissions}
              placement="inline"
            />
            <div className="lg:hidden">
              <EnhancedTenantSwitcher
                tenants={tenants}
                activeTenantId={activeTenantId}
                onSwitch={handleTenantSwitch}
              />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
